import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { supabase } from '../lib/supabaseClient';
import { useAuth } from '../context/AuthContext';
import { resolveTierState, warnUnmappedPrice } from '../config/tiers';
import type { Tier } from '../config/tiers';

/**
 * Pro subscription state for the logged-in user, sourced from the
 * public.user_subscriptions row (RLS lets a user read only their own row).
 *
 * Exposed as a small provider + hook so every consumer (header Upgrade button,
 * checkout-return notice, future gated features) shares ONE fetch and a single
 * refetch — so when the row changes, refetching in one place updates the whole
 * app. An in-place upgrade writes that row server-side before responding (see
 * api/create-checkout-session.js), so refetching on the response is enough; the
 * Stripe webhook confirms the same values shortly after.
 *
 * TIERS. Every gate reads a CAPABILITY — `canPlan` for the Strategy tab,
 * `canTax` for the Tax tab — rather than a single paid/not-paid boolean. The
 * capability comes from the subscription's Stripe price (see config/tiers.ts).
 *
 * The former `isPro` ("status is active or trialing") is gone: with three tiers
 * it answered the wrong question, since it could not distinguish what someone had
 * bought. `isSubscribed` covers the cases that genuinely asked "are they paying
 * at all" — opening the billing portal, skipping a redundant checkout, waiting for
 * a payment to land.
 */

/** Stripe subscription status mirrored into user_subscriptions.status. */
export type SubscriptionStatus =
  | 'none'
  | 'active'
  | 'trialing'
  | 'past_due'
  | 'canceled'
  | 'incomplete'
  | 'incomplete_expired'
  | 'unpaid'
  | string;

interface SubscriptionContextValue {
  /** Which tier the subscription's price maps to. 'free' when not paying. */
  tier: Tier;
  /**
   * True when there is a paid subscription of ANY tier.
   *
   * The right question for "do they already pay" — whether to start a checkout,
   * whether there is a billing portal to open, whether a just-completed payment
   * has landed yet. Distinct from `canTax`, which asks what they bought.
   */
  isSubscribed: boolean;
  /** Strategy tab: Plus and above. */
  canPlan: boolean;
  /** Tax tab: Pro only. */
  canTax: boolean;
  /** The subscription's Stripe price id, or null. Exposed for diagnostics. */
  priceId: string | null;
  /** Raw subscription status, or null when unknown / logged out. */
  status: SubscriptionStatus | null;
  /** ISO timestamp the current paid period ends, or null. */
  currentPeriodEnd: string | null;
  /** True when the subscription is set to end at the period end (customer cancelled
   *  but access persists until currentPeriodEnd). Defaults to false. */
  cancelAtPeriodEnd: boolean;
  /** True until the first fetch resolves. */
  loading: boolean;
  /** Re-read the row from Supabase (e.g. after returning from Checkout). */
  refetch: () => Promise<void>;
}

const SubscriptionContext = createContext<SubscriptionContextValue>({
  tier: 'free',
  isSubscribed: false,
  canPlan: false,
  canTax: false,
  priceId: null,
  status: null,
  currentPeriodEnd: null,
  cancelAtPeriodEnd: false,
  loading: true,
  refetch: async () => {},
});

export function SubscriptionProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const userId = user?.id ?? null;

  const [status, setStatus] = useState<SubscriptionStatus | null>(null);
  const [priceId, setPriceId] = useState<string | null>(null);
  const [currentPeriodEnd, setCurrentPeriodEnd] = useState<string | null>(null);
  const [cancelAtPeriodEnd, setCancelAtPeriodEnd] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchSubscription = useCallback(async () => {
    if (!userId) {
      setStatus(null);
      setPriceId(null);
      setCurrentPeriodEnd(null);
      setCancelAtPeriodEnd(false);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('user_subscriptions')
        .select('status, price_id, current_period_end, cancel_at_period_end')
        .eq('user_id', userId)
        .maybeSingle();

      if (error) {
        // No row yet, RLS, or a transient error — treat as "not subscribed"
        // rather than crashing. The webhook creates/updates the row.
        setStatus('none');
        setPriceId(null);
        setCurrentPeriodEnd(null);
        setCancelAtPeriodEnd(false);
      } else {
        setStatus((data?.status as SubscriptionStatus) ?? 'none');
        setPriceId((data?.price_id as string | null) ?? null);
        setCurrentPeriodEnd(data?.current_period_end ?? null);
        setCancelAtPeriodEnd(!!data?.cancel_at_period_end);
      }
    } catch {
      setStatus('none');
      setPriceId(null);
      setCurrentPeriodEnd(null);
      setCancelAtPeriodEnd(false);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  // Re-fetch whenever the logged-in user changes (login / logout / switch).
  useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription]);

  // Derived from the price, not the status.
  const { tier, canPlan, canTax, assumed } = resolveTierState(status, priceId);

  // A paid subscription whose price is not in the map resolves to Pro so nobody
  // is locked out of what they paid for. Say so once, or the gap is invisible.
  useEffect(() => {
    if (assumed) warnUnmappedPrice(priceId);
  }, [assumed, priceId]);

  const value: SubscriptionContextValue = {
    tier,
    isSubscribed: tier !== 'free',
    canPlan,
    canTax,
    priceId,
    status,
    currentPeriodEnd,
    cancelAtPeriodEnd,
    loading,
    refetch: fetchSubscription,
  };

  return React.createElement(SubscriptionContext.Provider, { value }, children);
}

export function useSubscription(): SubscriptionContextValue {
  return useContext(SubscriptionContext);
}
